import { Subscription } from 'rxjs';
import { LoaderService } from '../../services/loader.service';
import { Component, OnInit, OnDestroy, AfterViewInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { PageService } from '../../services/page.service';
import { MetaService } from '../../services/meta.service';
import { Globals } from '../../globals';
import { fade } from '../../animations/fade';
import { Store } from '@ngrx/store';
import { environment } from '../../../environments/environment';
import { FirstPageLoadService } from 'app/services/firstSiteLoad.service';
import { distinctUntilChanged } from 'rxjs/operators';
import { ConfigService } from 'app/services/config.service';
import { LocalStorageService } from 'app/services/localStorage.service';

// eslint-disable-next-line @typescript-eslint/ban-types
// declare let gtag: Function;
declare let pendo: any;

@Component({
  selector: 'app-page',
  templateUrl: './page.component.html',
  styleUrls: ['./page.component.scss'],
  animations: [fade],
  standalone: false,
})
export class PageComponent implements OnInit, OnDestroy, AfterViewInit {
  // Env
  env = environment;
  isIEOrEdge = /msie\s|trident\/|edge\//i.test(window.navigator.userAgent);
  meta = {};
  categories = {};
  body = '';
  hasContent = true;
  loadingContent = true;
  skeletonTemplate: string | null = null;
  isSkeletonTemplateWithLeftNav = false;
  transitionState = 'complete';
  pageRoute = '';
  staticPage = '';
  pageTypes = [];
  firstPageLoad = true;
  isSecNav = false;
  isDocIndex = false;
  navMenuItems = [];
  pageSubscription: Subscription;
  getapikeyInfoModal = false;
  routeRedocSubscription = 0;
  sidebarShow: boolean;
  isSpecPage = false;
  isApiMaticSpec = false;
  routeUrl = '';
  apiFourOFour = false;
  contactSales = false;
  isSidebarPresent = false;

  constructor(
    public globals: Globals,
    public store: Store<any>,
    public loaderService: LoaderService,
    private pageService: PageService,
    private metaService: MetaService,
    private route: ActivatedRoute,
    private firstPageLoadService: FirstPageLoadService,
    private router: Router,
    private configService: ConfigService,
    private localStorageService: LocalStorageService
  ) {}

  ngOnDestroy() {
    if (this.pageSubscription) {
      this.pageSubscription.unsubscribe();
    }
  }

  ngAfterViewInit(): void {
    if (this.router.url) {
      const urlParam = this.router.url.split('#id_token');
      if (urlParam.length >= 2) {
        window.location.href = '/';
      }
    }
  }

  private isSpecOnVersion() {
    const urlPartsFragments = this.router.url.split('/');
    return urlPartsFragments.length > 5;
  }

  private specVersion(productName: string) {
    const urlParts = this.router.url.split('/');
    if (urlParts[urlParts.length - 1] === 'api-specification' || urlParts[urlParts.length - 1] === productName) {
      return '';
    } else {
      return '/' + urlParts[urlParts.length - 1];
    }
  }

  private setSkeletonTemplateState(url: string): void {
    this.skeletonTemplate = this.globals.inferSkeletonTemplate(url);
    this.isSkeletonTemplateWithLeftNav =
      this.skeletonTemplate === this.globals.TEMPLATE_TYPES.PRODUCT ||
      this.skeletonTemplate === this.globals.TEMPLATE_TYPES.API_OVERVIEW ||
      this.skeletonTemplate === this.globals.TEMPLATE_TYPES.REDOC ||
      this.skeletonTemplate === this.globals.TEMPLATE_TYPES.DOC_PAGE ||
      this.skeletonTemplate === this.globals.TEMPLATE_TYPES.CONTACT_PAGE ||
      this.skeletonTemplate === this.globals.TEMPLATE_TYPES.CONTACT_SALES ||
      this.skeletonTemplate === this.globals.TEMPLATE_TYPES.PRODUCT_LANDING ||
      this.skeletonTemplate === this.globals.TEMPLATE_TYPES.CATEGORY;
  }

  ngOnInit() {
    this.route.url.subscribe(() => {
      window.scrollTo({
        top: 0,
        behavior: 'auto',
      });
      this.loaderService.hideInitialLoader();
      this.setSkeletonTemplateState(this.router.url);
      // let intervalRunCount = 0;
      // const pendoTimeInterval = setInterval(() => {
      //   intervalRunCount++;
      //   const pendoStyleElement = document.querySelectorAll('style[type="text/css"]');
      //   if (pendoStyleElement.length > 0) {
      //     clearInterval(pendoTimeInterval);
      //     pendoStyleElement.forEach((element) => {
      //       const elementClass = element.getAttribute('class');
      //       if (elementClass?.includes('pendo-style')) {
      //         element.remove();
      //       }
      //     });
      //   } else {
      //     if (intervalRunCount > 9) {
      //       clearInterval(pendoTimeInterval);
      //     }
      //   }
      // }, 1000);
      this.resetPendoFeedback();
      if (this.router.url.includes('/api-specification')) {
        this.isSpecPage = true;
        this.isApiMaticSpec = false;
        this.routeUrl = `/${this.route.snapshot.url.map((o) => o.path).join('/')}`.split('?').shift();
        this.configService.getConfig('apimatic-products').subscribe(
          (apimaticProductsResp) => {
            const apiMaticProducts = apimaticProductsResp.Items[0].apimatic;
            apiMaticProducts.forEach((item) => {
              const apiLink = item.family ? `${item.family}/${item.name}` : item.name;
              if (this.router.url.includes(`${apiLink}/api-specification`)) {
                const secondaryNav = [
                  {
                    title: 'Documentation',
                    path: item.family ? '/apis/' + item.family + '/' + item.name : '/apis/' + item.name,
                  },
                  {
                    title: 'API Specification',
                    path: item.family
                      ? '/apis/' + item.family + '/' + item.name + '/api-specification'
                      : '/apis/' + item.name,
                  },
                ];
                const meta = {
                  versions: item.versions,
                  template: 'redoc',
                  title: item.name,
                  'secondary-nav':
                    item['secondary-nav'] && item['secondary-nav'].length > 0 ? item['secondary-nav'] : secondaryNav,
                };

                if (item['new-onboarding'] || item['migrated']) {
                  this.isApiMaticSpec = true;
                  meta['apimatic-spec'] = true;
                  this.update({ metadata: meta, categories: null, body: null, title: null });
                  if (item.family) {
                    this.router.navigateByUrl(
                      `/api-specification/${item.family}/${item.name}${this.specVersion(item.name)}`
                    );
                  } else {
                    this.router.navigateByUrl(`/api-specification/${item.name}${this.specVersion(item.name)}`);
                  }
                  return;
                } else {
                  if (this.isSpecOnVersion()) {
                    this.isApiMaticSpec = false;
                    this.getPageInfo(this.routeUrl);
                    return;
                  } else {
                    this.isApiMaticSpec = true;
                    meta['apimatic-spec'] = true;
                    this.update({ metadata: meta, categories: null, body: null, title: null });
                    if (item.family) {
                      this.router.navigateByUrl(`/api-specification/${item.family}/${item.name}`);
                    } else {
                      this.router.navigateByUrl(`/api-specification/${item.name}`);
                    }
                    return;
                  }
                }
              }
            });

            if (!this.isApiMaticSpec) {
              this.routeUrl = `/${this.route.snapshot.url.map((o) => o.path).join('/')}`.split('?').shift();
              this.getPageInfo(this.routeUrl);
            }
          },
          (error) => {
            this.router.navigateByUrl('/404', { skipLocationChange: true });
          }
        );
      } else {
        this.isSpecPage = false;
        this.isApiMaticSpec = false;
      }

      if (!this.contactSales && !this.isSpecPage) {
        this.routeUrl = `/${this.route.snapshot.url.map((o) => o.path).join('/')}`.split('?').shift();
        this.getPageInfo(this.routeUrl);
      }
    });

    this.metaService.metadata.pipe(distinctUntilChanged()).subscribe((pMeta) => {
      this.isSecNav = pMeta && (pMeta['secondary-nav'] || pMeta['sec-nav']);
      this.isSidebarPresent = pMeta && pMeta['sidebar-menu'] === undefined ? false : pMeta['sidebar-menu'];
      this.isDocIndex = pMeta && pMeta['page-index'] === undefined ? false : pMeta['page-index'];
    });
    this.pageService.getApiKeyConfirmEvent.subscribe(() => {
      this.getapikeyInfoModal = true;
    });
  }

  sidebarShowHandler(sidebarShow: boolean) {
    this.sidebarShow = sidebarShow;
  }

  isDsTemplate(template: string | undefined): boolean {
    return (
      template === this.globals.TEMPLATE_TYPES.API_OVERVIEW_FAMILY ||
      template === this.globals.TEMPLATE_TYPES.CATEGORY ||
      template === this.globals.TEMPLATE_TYPES.APP_PRODUCT_LANDING ||
      template === this.globals.TEMPLATE_TYPES.APP_API_DOC
    );
  }

  get showDocSecHeaderSkeletonLoader(): boolean {
    return this.globals.showDocSecHeaderSkeletonLoader(this.meta, this.router.url, this.skeletonTemplate);
  }

  getPageInfo(routeUrl: string) {
    this.pageRoute = routeUrl;
    if (this.pageSubscription) {
      this.pageSubscription.unsubscribe();
    }
    this.body = '';
    this.setSkeletonTemplateState(routeUrl);

    if (this.skeletonTemplate === this.globals.TEMPLATE_TYPES.ALL_PRODUCT) {
      this.meta = { template: this.globals.TEMPLATE_TYPES.ALL_PRODUCT };
    }

    this.pageSubscription = this.pageService.getPage(routeUrl, this.route.snapshot.queryParams.token).subscribe(
      (page) => {
        if (!page.Item) {
          this.localStorageService.setItem('previousPageUrl', this.pageRoute);
          this.router.navigateByUrl('/404', { skipLocationChange: true });
        } else {
          this.contactSales = page.Item.metadata?.template === this.globals.TEMPLATE_TYPES.CONTACT_SALES;
          this.update(page.Item);
          window.scrollTo({
            top: 0,
            behavior: 'auto',
          });
        }
      },
      (error) => {
        this.localStorageService.setItem('previousPageUrl', this.pageRoute);
        error.status === 403
          ? this.router.navigateByUrl('/403', { skipLocationChange: true })
          : this.router.navigateByUrl('/404', { skipLocationChange: true });
      }
    );
  }

  update(pageItem: { metadata: any; categories: any; body: string; title: string }) {
    this.sidebarShow = false;
    this.meta = pageItem.metadata || {};
    if (this.meta['template'] === 'redoc') {
      this.routeRedocSubscription++;
    }

    this.metaService.metadata.next(this.meta);
    this.categories = pageItem.categories || {};
    this.body = pageItem.body || '';
    this.hasContent = true;
    this.metaService.setTitle([pageItem.title]);
    // if ('metadata' in pageItem) {
    //   this.metaService.setAllMeta('seo' in pageItem.metadata ? pageItem.metadata.seo : []);
    // }
  }

  private resetPendoFeedback() {
    const guideElement = document.querySelector('._pendo-guide-walkthrough_');
    if (guideElement) {
      const thankyouButton = guideElement.querySelector('.-thankyou-button');
      if (thankyouButton) {
        pendo.onGuideAdvanced();
      }
    }
  }

  private hidePendoGuide() {
    const guideChecker = setInterval(() => {
      const guideElement: HTMLElement | null = document.querySelector('._pendo-guide-walkthrough_');
      if (guideElement) {
        guideElement.style.display = 'none';
        clearInterval(guideChecker);
      }
    }, 1000);
  }

  // Add googleAnalytics() to (page) and (error) above
  // googleAnalytics() {
  //   if (environment.googleAnalytics !== '') {
  //     gtag('config', environment.googleAnalytics, {
  //       'page_path': window.location.pathname
  //     });
  //   }
  // }
  closeInfoModal(): void {
    this.getapikeyInfoModal = false;
  }
}
